import { useClient, useProperties } from '@/api/hooks';
import { appNavItems, filterNavByModules } from '@/components/layout/navItems';
import { HoverTooltip } from '@/components/ui/HoverTooltip';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n';
import { ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { shellHeaderClass, sidebarCollapsedWidthClass, sidebarExpandedWidthClass } from './shellLayout';

const STORAGE_KEY = 'manejai_sidebar_collapsed';

export function Sidebar() {
  const { data: client } = useClient();
  const { propertyId } = usePropertyScope();
  const { data: properties } = useProperties();
  const t = useT();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === '1',
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  const brandLabel = client?.trade_name || client?.legal_name || 'Manejai';
  const activities = useMemo(
    () => properties?.properties.find((p) => p.id === propertyId)?.activities ?? [],
    [properties, propertyId],
  );
  const navItems = filterNavByModules(appNavItems, client?.modules, activities);

  return (
    <div
      className={cn(
        'relative z-40 hidden lg:sticky lg:top-0 lg:flex h-screen shrink-0 transition-[width] duration-200',
        collapsed ? sidebarCollapsedWidthClass : sidebarExpandedWidthClass,
      )}
    >
      <aside className="flex h-full w-full flex-col border-r border-manejai-sand bg-white/80 backdrop-blur-sm">
        <div
          className={cn(
            'relative flex items-center',
            shellHeaderClass,
            collapsed ? 'justify-center px-2' : 'px-5',
          )}
        >
          <SidebarBrand
            collapsed={collapsed}
            logoUrl={client?.logo_url}
            brandLabel={brandLabel}
            tagline={t('nav.brand.tagline')}
          />
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className={cn(
              'absolute bottom-0 right-0 z-50 flex h-7 w-7 translate-x-1/2 translate-y-1/2 items-center justify-center',
              'rounded-full border border-manejai-sand bg-white text-manejai-bark shadow-sm transition-colors',
              'hover:border-manejai-sage hover:bg-manejai-cream hover:text-manejai-forest',
            )}
            aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
            title={collapsed ? t('nav.expand') : t('nav.collapse')}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronLeft className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>

        <nav
          className={cn(
            'flex-1 min-h-0 overflow-y-auto py-4 space-y-1',
            collapsed ? 'px-2' : 'px-3',
          )}
        >
          {navItems.map(({ to, icon: Icon, labelKey, end }) => (
            <SidebarNavItem
              key={to}
              to={to}
              end={end ?? to === '/'}
              icon={Icon}
              label={t(labelKey)}
              collapsed={collapsed}
            />
          ))}
        </nav>

        <div
          className={cn(
            'shrink-0 border-t border-manejai-sand/60 py-3',
            collapsed ? 'px-2' : 'px-4',
          )}
        >
          <p
            className={cn(
              'text-center text-manejai-bark/35',
              collapsed ? 'text-[9px] leading-tight' : 'text-[10px] tracking-wide',
            )}
          >
            {collapsed ? 'Manejai' : t('nav.footer')}
          </p>
        </div>
      </aside>
    </div>
  );
}

function SidebarBrand({
  collapsed,
  logoUrl,
  brandLabel,
  tagline,
}: {
  collapsed: boolean;
  logoUrl?: string | null;
  brandLabel: string;
  tagline: string;
}) {
  if (logoUrl) {
    return (
      <HoverTooltip
        label={brandLabel}
        enabled={collapsed}
        className={collapsed ? undefined : 'min-w-0 flex-1'}
      >
        <div
          className={cn(
            'flex items-center justify-center rounded-lg bg-white px-2',
            collapsed ? 'h-10 w-10' : 'h-12 w-full max-w-[11rem]',
          )}
        >
          <img
            src={logoUrl}
            alt={brandLabel}
            className={cn(
              'object-contain',
              collapsed ? 'h-8 w-8' : 'max-h-10 max-w-full',
            )}
          />
        </div>
      </HoverTooltip>
    );
  }

  if (collapsed) {
    return (
      <HoverTooltip label="Manejai">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-manejai-forest text-white">
          <Home className="h-5 w-5" />
        </div>
      </HoverTooltip>
    );
  }

  return (
    <div className="min-w-0 flex-1">
      <p className="font-display text-lg font-semibold text-manejai-forest leading-tight">
        Manejai
      </p>
      <p className="text-xs text-manejai-bark/60">{tagline}</p>
    </div>
  );
}

function SidebarNavItem({
  to,
  end,
  icon: Icon,
  label,
  collapsed,
}: {
  to: string;
  end?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  collapsed: boolean;
}) {
  const link = (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'relative flex items-center rounded-xl py-2.5 text-sm font-medium transition-colors',
          collapsed ? 'justify-center px-2' : 'gap-3 px-3',
          isActive
            ? 'bg-manejai-forest text-white shadow-sm'
            : 'text-manejai-bark hover:bg-manejai-sand/50',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="flex-1">{label}</span>}
    </NavLink>
  );

  return (
    <HoverTooltip label={label} enabled={collapsed} className="block">
      {link}
    </HoverTooltip>
  );
}
